import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const HISTORY_API =
  "http://127.0.0.1:8000/api/medcharts/fluid-therapy-patient-history/?format=json";

const pageStyle = {
  minHeight: "100vh",
  background: Colors.bg.app,
  padding: "32px 18px",
};

const wrapperStyle = {
  maxWidth: "1380px",
  margin: "0 auto",
  background: Colors.bg.paper,
  borderRadius: "24px",
  boxShadow: Colors.shadow.brand,
  overflow: "hidden",
  border: `1px solid ${Colors.border.light}`,
};

const sectionCardStyle = {
  background: Colors.bg.paper,
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 4px 14px rgba(11,60,63,0.05)",
};

const tableWrapStyle = {
  overflowX: "auto",
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "16px",
  background: Colors.bg.paper,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "1250px",
};

const thStyle = {
  textAlign: "left",
  padding: "14px",
  background: Colors.bg.hover,
  color: Colors.text.header,
  fontSize: "13px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontWeight: 800,
};

const tdStyle = {
  padding: "14px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontSize: "14px",
  color: Colors.text.body,
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function getFluidLabel(fluid) {
  const map = {
    NS: "Normal Saline",
    RL: "Ringer Lactate",
    DNS: "Dextrose Normal Saline",
    D5: "5% Dextrose",
    D10: "10% Dextrose",
    OTHER: "Other",
  };
  return map[fluid] || fluid || "-";
}

export default function FluidTherapyPatientHistoryDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientUhid } = useParams();

  const passedPatient = location.state?.patient || null;

  const [selectedPatient, setSelectedPatient] = useState(passedPatient);
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (patientUhid) {
      fetchPatientHistory(patientUhid);
    }
  }, [patientUhid]);

  const fetchPatientHistory = async (uhid) => {
    try {
      setLoadingHistory(true);
      setErrorMsg("");

      const url = `${HISTORY_API}&patient_uhid=${encodeURIComponent(uhid)}`;
      const res = await axios.get(url);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setHistoryRows(data);

      if (!passedPatient && data.length > 0) {
        setSelectedPatient({
          patient_uhid: data[0].patient_uhid,
          patient_name: data[0].patient_name,
          total_fluid_times: data.length,
          last_fluid_at: data[0].created_at,
        });
      }
    } catch (error) {
      console.error("Patient history fetch error:", error);
      setErrorMsg("Unable to load patient history.");
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={wrapperStyle}>
        <div
          style={{
            padding: "26px 30px 18px",
            background: Colors.bg.paper,
            borderBottom: `1px solid ${Colors.border.light}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: Colors.text.title }}>
                Patient Fluid Therapy History
              </h2>
              <p style={{ margin: "8px 0 0", color: Colors.text.muted, fontSize: "14px" }}>
                Full fluid therapy history for the selected patient.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate(ROUTES.fluid_therapy_history)}
              style={{
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                fontWeight: 700,
                cursor: "pointer",
                padding: "12px 16px",
              }}
            >
              Back to Summary
            </button>
          </div>

          <div
            style={{
              marginTop: "16px",
              height: "6px",
              width: "180px",
              borderRadius: "999px",
              background: Colors.brand.accentBar,
            }}
          />
        </div>

        <div style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {errorMsg}
            </div>
          )}

          {selectedPatient && (
            <div style={{ ...sectionCardStyle, marginBottom: "24px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "14px",
                }}
              >
                <div style={{ background: Colors.bg.hover, border: `1px solid ${Colors.border.light}`, borderRadius: "14px", padding: "14px" }}>
                  <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Patient UHID</div>
                  <div style={{ color: Colors.text.title, fontWeight: 800, fontSize: "15px", marginTop: "4px" }}>
                    {selectedPatient.patient_uhid || "-"}
                  </div>
                </div>

                <div style={{ background: Colors.bg.hover, border: `1px solid ${Colors.border.light}`, borderRadius: "14px", padding: "14px" }}>
                  <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Patient Name</div>
                  <div style={{ color: Colors.text.title, fontWeight: 800, fontSize: "15px", marginTop: "4px" }}>
                    {selectedPatient.patient_name || "-"}
                  </div>
                </div>

                <div style={{ background: Colors.bg.hover, border: `1px solid ${Colors.border.light}`, borderRadius: "14px", padding: "14px" }}>
                  <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Total Fluid Times</div>
                  <div style={{ color: Colors.text.title, fontWeight: 800, fontSize: "15px", marginTop: "4px" }}>
                    {selectedPatient.total_fluid_times || historyRows.length || 0}
                  </div>
                </div>

                <div style={{ background: Colors.bg.hover, border: `1px solid ${Colors.border.light}`, borderRadius: "14px", padding: "14px" }}>
                  <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Last Fluid Date</div>
                  <div style={{ color: Colors.text.title, fontWeight: 800, fontSize: "15px", marginTop: "4px" }}>
                    {formatDateTime(selectedPatient.last_fluid_at)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={sectionCardStyle}>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Admission No</th>
                    <th style={thStyle}>Patient UHID</th>
                    <th style={thStyle}>Patient Name</th>
                    <th style={thStyle}>Doctor Name</th>
                    <th style={thStyle}>Fluid</th>
                    <th style={thStyle}>Qty</th>
                    <th style={thStyle}>Speed</th>
                    <th style={thStyle}>Frequency</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr>
                      <td colSpan="10" style={tdStyle}>Loading history...</td>
                    </tr>
                  ) : historyRows.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={tdStyle}>No history found.</td>
                    </tr>
                  ) : (
                    historyRows.map((row) => (
                      <tr key={row.id}>
                        <td style={tdStyle}>{row.admission_number || "-"}</td>
                        <td style={tdStyle}>{row.patient_uhid || "-"}</td>
                        <td style={tdStyle}>{row.patient_name || "-"}</td>
                        <td style={tdStyle}>{row.doctor_name || "-"}</td>
                        <td style={tdStyle}>{getFluidLabel(row.fluid)}</td>
                        <td style={tdStyle}>{row.qty_ml || "-"}</td>
                        <td style={tdStyle}>{row.speed_ml_hr || "-"}</td>
                        <td style={tdStyle}>{row.frequency || "-"}</td>
                        <td style={tdStyle}>{row.duration || "-"}</td>
                        <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}